import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer, ScanBarcode, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, useCan } from "@/context/AuthContext";
import { usePrint } from "@/context/PrintContext";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { CameraScanButton } from "@/components/scan/CameraScanButton";
import { NoAccess } from "@/components/NoAccess";
import { formatMoney } from "@/lib/money";
import { Receipt } from "@/components/Receipt";
import { buildCreditNoteModel, buildReturnModel } from "@/lib/receipt";

const paidCum = (ln, n, incl) => Math.floor(((ln.taxable + (incl ? 0 : ln.tax)) * n) / ln.qty);
const CN_TONE = { active: "bg-emerald-100 text-emerald-800", redeemed: "bg-slate-200 text-slate-700", expired: "bg-rose-100 text-rose-700", cancelled: "bg-slate-100 text-slate-500" };
export const CnStatus = ({ s, id }) => <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${CN_TONE[s] || ""}`} data-testid={id}>{s}</span>;

function Lookup({ onFound }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const ref = useRef(null);
  const find = async (c) => {
    setError("");
    try {
      onFound((await api.get("/returns/lookup", { params: { code: c.trim() } })).data);
      setCode("");
    } catch (e) {
      setError(errMsg(e));
      onFound(null);
    }
  };
  useBarcodeScanner((c) => find(c));
  return (
    <form onSubmit={(e) => { e.preventDefault(); code.trim() && find(code); }} className="space-y-2">
      <div className="relative">
        <ScanBarcode className="absolute left-3 top-4 h-5 w-5 text-slate-400" />
        <input ref={ref} autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan the receipt barcode or type the receipt number" className="h-14 w-full rounded-xl border-2 border-slate-900 bg-white pl-11 pr-28 font-mono text-base uppercase outline-none" data-testid="return-lookup-input" />
        <button className="absolute right-2 top-2 h-10 rounded-lg bg-slate-900 px-4 text-sm font-bold text-white" data-testid="return-lookup-button">Find</button>
      </div>
      <CameraScanButton title="Scan receipt barcode" testid="return-camera-button" className="flex h-11 w-full gap-2"
        onDetect={(c) => { setCode(c.toUpperCase()); find(c); return { ok: true, label: c.toUpperCase() }; }} />
      {error && <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="return-lookup-error">{error}</div>}
    </form>
  );
}

function ReturnForm({ found, onDone }) {
  const { sale } = found;
  const dp = sale.decimals;
  const [rows, setRows] = useState(() => found.lines.map((l) => ({ qty: "0", condition: l.expired ? "damaged" : "resellable" })));
  const [method, setMethod] = useState("cash");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const refund = useMemo(() => found.lines.reduce((a, l, i) => {
    const k = Math.min(parseInt(rows[i].qty, 10) || 0, l.returnable);
    const ln = sale.lines[i];
    return a + paidCum(ln, l.returned + k, sale.tax_inclusive) - paidCum(ln, l.returned, sale.tax_inclusive);
  }, 0), [rows, found, sale]);
  const picked = found.lines.map((l, i) => ({ line_index: i, qty: parseInt(rows[i].qty, 10) || 0, condition: rows[i].condition })).filter((x) => x.qty > 0);
  const bad = found.lines.some((l, i) => (parseInt(rows[i].qty, 10) || 0) > l.returnable);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/returns", { receipt_code: sale.receipt_code, lines: picked, method, reason });
      toast.success(method === "cash" ? `Cash refunded ${sale.currency} ${data.refund_str}` : `Credit note ${data.credit_note.number} issued`);
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="return-form">
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="font-mono text-lg font-bold" data-testid="return-sale-code">{sale.receipt_code}</span>
        <span className="text-sm text-slate-500">{new Date(sale.created_at).toLocaleString("en-GB")} · {sale.currency} {sale.totals.total_str}</span>
      </div>
      <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
        {found.lines.map((l, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`return-line-${i}`}>
            <div className="min-w-[180px] flex-1">
              <div className="font-semibold text-slate-900">{l.name}</div>
              <div className="font-mono text-xs text-slate-500" data-testid={`return-line-${i}-counts`}>sold {l.sold} · returned {l.returned} · returnable {l.returnable}</div>
              {l.expired && <div className="text-xs font-semibold text-rose-600" data-testid={`return-line-${i}-expired`}>Expired batch — cannot be restocked</div>}
            </div>
            <input inputMode="numeric" disabled={!l.returnable} value={rows[i].qty} onChange={(e) => set(i, "qty", e.target.value)} className="h-11 w-20 rounded-xl border border-slate-200 px-3 text-right font-mono disabled:bg-slate-100" data-testid={`return-line-${i}-qty`} />
            <div className="flex rounded-xl bg-slate-100 p-1 text-xs font-bold">
              {[["resellable", "Resellable"], ["damaged", "Damaged"]].map(([k, lb]) => (
                <button key={k} type="button" disabled={l.expired && k === "resellable"} onClick={() => set(i, "condition", k)} data-testid={`return-line-${i}-${k}`}
                  className={`h-9 rounded-lg px-3 disabled:opacity-30 ${rows[i].condition === k ? "bg-slate-950 text-white" : "text-slate-600"}`}>{lb}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="Reason (optional)" className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" data-testid="return-reason" />
      <div className="grid grid-cols-2 gap-2">
        {[["cash", "Cash refund"], ["credit_note", "Credit note"]].map(([k, lb]) => (
          <button key={k} onClick={() => setMethod(k)} data-testid={`return-method-${k}`} className={`h-12 rounded-xl border-2 font-bold ${method === k ? "border-slate-900 bg-amber-50" : "border-slate-200 text-slate-500"}`}>{lb}</button>
        ))}
      </div>
      <div className="flex items-center justify-between rounded-2xl bg-slate-950 px-5 py-4 text-white">
        <span className="font-mono text-xs uppercase tracking-widest text-slate-400">Refund</span>
        <span className="money text-3xl font-extrabold text-amber-400" data-testid="return-refund-total">{sale.currency} {formatMoney(refund, dp)}</span>
      </div>
      {bad && <div className="text-sm font-semibold text-rose-600" data-testid="return-qty-error">You can't return more than the returnable quantity</div>}
      <button disabled={busy || !picked.length || bad} onClick={submit} className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 font-display text-lg font-bold text-white disabled:bg-slate-200 disabled:text-slate-400" data-testid="return-submit">
        <Undo2 className="h-5 w-5" /> Process return
      </button>
    </div>
  );
}

function Done({ ret, onNext }) {
  const { printReturn, printCreditNote } = usePrint();
  const { session } = useAuth();
  return (
    <div className="space-y-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="return-done">
      <div className="font-display text-2xl font-extrabold text-slate-900">{ret.number}</div>
      <div className="text-lg font-semibold text-emerald-800" data-testid="return-done-message">
        {ret.method === "cash" ? `Cash refunded ${ret.currency} ${ret.refund_str}` : `Credit note ${ret.credit_note.number} · ${ret.currency} ${ret.credit_note.amount_str} · expires ${ret.credit_note.expires_on}`}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => printReturn(ret)} className="flex h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-bold text-white" data-testid="return-print"><Printer className="h-4 w-4" />Print return receipt</button>
        {ret.credit_note && <button onClick={() => printCreditNote(ret.credit_note)} className="flex h-11 items-center gap-2 rounded-xl bg-amber-500 px-4 text-sm font-bold text-slate-950" data-testid="return-print-cn"><Printer className="h-4 w-4" />Print credit note</button>}
        <button onClick={onNext} className="h-11 rounded-xl border-2 border-slate-900 px-4 text-sm font-bold" data-testid="return-next">Next return</button>
      </div>
      <div className="flex flex-wrap items-start gap-4">
        <div className="w-[320px] rounded-xl bg-white p-3 shadow" data-testid="return-receipt-preview"><Receipt model={buildReturnModel(ret, session.business)} /></div>
        {ret.credit_note && <div className="w-[320px] rounded-xl bg-white p-3 shadow" data-testid="cn-preview"><Receipt model={buildCreditNoteModel(ret.credit_note, session.business)} /></div>}
      </div>
    </div>
  );
}

function ReturnsList() {
  const [rows, setRows] = useState([]);
  const { printReturn, printCreditNote } = usePrint();
  useEffect(() => { api.get("/returns").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  return (
    <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="returns-list">
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`returns-row-${r.number}`}>
          <span className="font-mono font-bold">{r.number}</span>
          <span className="font-mono text-xs text-slate-500">from {r.receipt_code} · {r.created_at.slice(0, 10)}</span>
          <span className="text-sm">{r.method === "cash" ? "Cash" : r.credit_note?.number}</span>
          {r.credit_note && <CnStatus s={r.credit_note.status} id={`returns-cn-status-${r.number}`} />}
          <span className="ml-auto font-mono font-bold">{r.currency} {r.refund_str}</span>
          <button onClick={() => printReturn(r)} className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-bold" data-testid={`returns-reprint-${r.number}`}>Reprint</button>
          {r.credit_note && <button onClick={() => printCreditNote(r.credit_note)} className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-bold" data-testid={`returns-reprint-cn-${r.number}`}>Credit note</button>}
        </div>
      ))}
      {!rows.length && <div className="p-10 text-center text-sm text-slate-400">No returns yet</div>}
    </div>
  );
}

function CnLookup() {
  const [code, setCode] = useState("");
  const [cn, setCn] = useState(null);
  const [error, setError] = useState("");
  const { printCreditNote } = usePrint();
  const find = async (e) => {
    e.preventDefault();
    setError("");
    setCn(null);
    try { setCn((await api.get(`/credit-notes/${encodeURIComponent(code.trim())}`)).data); } catch (ex) { setError(errMsg(ex)); }
  };
  return (
    <div className="space-y-3">
      <form onSubmit={find} className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="CN-…" className="h-12 flex-1 rounded-xl border-2 border-slate-900 bg-white px-3 font-mono uppercase" data-testid="cn-lookup-input" />
        <button className="h-12 rounded-xl bg-slate-900 px-5 font-bold text-white" data-testid="cn-lookup-button">Look up</button>
      </form>
      {error && <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="cn-lookup-error">{error}</div>}
      {cn && (
        <div className="space-y-1 rounded-2xl border border-slate-200 bg-white p-5" data-testid="cn-lookup-result">
          <div className="flex items-center gap-3"><span className="font-mono text-lg font-bold">{cn.number}</span><CnStatus s={cn.status} id="cn-lookup-status" />
            <span className="ml-auto money text-xl font-extrabold">{cn.currency} {cn.amount_str}</span></div>
          <div className="text-sm text-slate-600">Issued {cn.issued_at.slice(0, 10)} · expires {cn.expires_on} · from receipt {cn.receipt_code} · valid at {cn.branch_name} only</div>
          {cn.redeemed_receipt && <div className="text-sm text-slate-600">Used on {cn.redeemed_receipt}</div>}
          <button onClick={() => printCreditNote(cn)} className="mt-2 h-10 rounded-xl border-2 border-slate-900 px-4 text-sm font-bold" data-testid="cn-lookup-print">Reprint</button>
        </div>
      )}
    </div>
  );
}

const TABS = [["process", "Process return"], ["list", "Returns"], ["credit-notes", "Credit notes"]];

export default function Returns() {
  const { session } = useAuth();
  const can = useCan();
  const [sp, setSp] = useSearchParams();
  const [found, setFound] = useState(null);
  const [done, setDone] = useState(null);
  if (!can("returns")) return <NoAccess what="Returns" />;
  const tab = sp.get("tab") || "process";
  return (
    <div className="h-full overflow-y-auto" data-testid="returns-page">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8">
        <div className="eyebrow">Returns · {session.branch.name} · {session.business.return_days ?? 14}-day return period</div>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900">Returns & credit notes</h1>
        <div className="mt-5 flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1" role="tablist">
          {TABS.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setSp({ tab: id })} data-testid={`returns-tab-${id}`}
              className={`h-10 shrink-0 rounded-lg px-4 text-sm font-bold ${tab === id ? "bg-slate-950 text-white" : "text-slate-600"}`}>{label}</button>
          ))}
        </div>
        <div className="mt-6 space-y-5">
          {tab === "process" && (done ? <Done ret={done} onNext={() => { setDone(null); setFound(null); }} /> : (
            <>
              <Lookup onFound={setFound} />
              {found && <ReturnForm key={found.sale.id} found={found} onDone={setDone} />}
            </>
          ))}
          {tab === "list" && <ReturnsList />}
          {tab === "credit-notes" && <CnLookup />}
        </div>
      </div>
    </div>
  );
}
